import { useEffect, useState } from "react";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Check, Send, Ban, Eye, Trash2, Save } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Chasers() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null); // chaser being edited
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const params = status !== "all" ? { status } : {};
    const [r, s] = await Promise.all([api.get("/chasers", { params }), api.get("/settings")]);
    setRows(r.data);
    setSettings(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const approve = async (id) => { await api.post(`/chasers/${id}/approve`); toast.success("Chaser approved"); load(); };
  const cancel = async (id) => { await api.post(`/chasers/${id}/cancel`); toast.success("Chaser cancelled"); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete this chaser?")) return;
    try { await api.delete(`/chasers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const send = async (id) => {
    try {
      const r = await api.post(`/chasers/${id}/send`);
      if (r.data.status === "sent") toast.success("Chaser sent (mocked)");
      else if (r.data.status === "blocked") toast.error(`Blocked: ${r.data.reason}`);
      else toast.error(`Failed: ${r.data.reason || "unknown"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitForApproval = async (id) => {
    await api.patch(`/chasers/${id}`, { status: "pending_approval" });
    toast.success("Sent for approval");
    load();
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/chasers/${editing.chaser_id}`, {
        subject: editing.subject,
        body: editing.body,
      });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllOnPage = () => setSelected((s) => {
    const allIds = rows.map((c) => c.chaser_id);
    const allSelected = allIds.every((id) => s.has(id));
    if (allSelected) { const n = new Set(s); allIds.forEach((id) => n.delete(id)); return n; }
    const n = new Set(s); allIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/chasers/bulk-delete", { ids: Array.from(selected) });
      const skipped = r.data.sent_deleted || 0;
      toast.success(`Deleted ${r.data.deleted} chaser${r.data.deleted === 1 ? "" : "s"}${skipped ? ` · incl. ${skipped} sent (audit-logged)` : ""}`);
      clearSelection();
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader
        testid="page-chasers"
        title="Client chasers"
        subtitle={settings ? (settings.test_mode ? `Review mode · routed to ${settings.admin_test_email}` : "Live · sending direct to clients") : "Loading…"}
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="chasers-status-filter" className="w-52 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500">{rows.length} chaser(s)</div>
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="chasers-bulk-bar" className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm">
              <span data-testid="chasers-selected-count">
                <span className="font-semibold">{selected.size}</span> selected
              </span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white text-xs" data-testid="chasers-clear-selection">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-8" data-testid="chasers-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <Checkbox
                    data-testid="chasers-select-all"
                    checked={rows.length > 0 && rows.every((c) => selected.has(c.chaser_id))}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all chasers on this page"
                  />
                </th>
                <th className="text-left px-4 py-2">Client · Job</th>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No chasers yet. Create one from a client's page.</td></tr>}
              {rows.map((c) => (
                <tr key={c.chaser_id} data-testid="chaser-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(c.chaser_id) ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid={`chaser-row-select-${c.chaser_id}`}
                      checked={selected.has(c.chaser_id)}
                      onCheckedChange={() => toggleOne(c.chaser_id)}
                      aria-label={`Select chaser ${c.subject}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{c.client_name}</div>
                    <div className="text-xs text-slate-500">{c.job_title || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{c.to_email}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-md truncate">{c.subject}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDateTime(c.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid="chaser-preview-btn" onClick={() => setEditing({ ...c, viewOnly: c.status === "sent" || c.status === "cancelled" })} title="Preview / edit" className="p-1.5 text-slate-500 hover:text-slate-900 rounded transition-colors"><Eye className="h-4 w-4" /></button>
                      {c.status === "draft" && (
                        <button data-testid="chaser-submit-btn" onClick={() => submitForApproval(c.chaser_id)} title="Submit for approval" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"><Save className="h-4 w-4" /></button>
                      )}
                      {c.status === "pending_approval" && (
                        <button data-testid="chaser-approve-btn" onClick={() => approve(c.chaser_id)} title="Approve" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"><Check className="h-4 w-4" /></button>
                      )}
                      {(c.status === "approved" || (!settings?.require_approval && (c.status === "draft" || c.status === "pending_approval"))) && (
                        <button data-testid="chaser-send-btn" onClick={() => send(c.chaser_id)} title="Send now" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"><Send className="h-4 w-4" /></button>
                      )}
                      {c.status !== "sent" && c.status !== "cancelled" && (
                        <button data-testid="chaser-cancel-btn" onClick={() => cancel(c.chaser_id)} title="Cancel" className="p-1.5 text-slate-500 hover:text-rose-600 rounded transition-colors"><Ban className="h-4 w-4" /></button>
                      )}
                      {c.status !== "sent" && (
                        <button data-testid="chaser-delete-btn" onClick={() => del(c.chaser_id)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-600 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="chasers-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} chaser{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Drafts, pending, approved, blocked, cancelled AND already-sent chasers in your selection will be permanently removed. Sent deletions are audit-logged. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="chasers-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="chaser-editor" className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.viewOnly ? "Chaser (read-only)" : "Edit chaser"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 items-center">
                <div className="text-slate-500">To</div>
                <div className="col-span-3 font-mono text-xs">{editing.to_email} {editing.to_name && <span className="text-slate-400">({editing.to_name})</span>}</div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input data-testid="chaser-subject-input" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} disabled={editing.viewOnly} />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea data-testid="chaser-body-input" rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} disabled={editing.viewOnly} />
              </div>
              {settings?.test_mode && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Review mode is on — this chaser will be routed to <span className="font-mono">{settings.admin_test_email}</span> with a <span className="font-mono">[Taxegy review]</span> subject prefix so you can forward it manually.
                </div>
              )}
              <div className="text-[11px] text-slate-500">Tax identifiers (UTR, Companies House number) are automatically redacted when the chaser is sent.</div>
            </div>
          )}
          <DialogFooter>
            {!editing?.viewOnly && (
              <Button data-testid="chaser-save-btn" onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800">Save changes</Button>
            )}
            <Button variant="outline" onClick={() => setEditing(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
